// @flow
import React from 'react';
import type { Node } from 'react';

import ResidentService from '../../services/residentService';
import useIntl from '../../hooks/useIntl';
import { impactMessages, moveOutImpactMessages as messages } from './messages';
import {
  CertificationImpactRow,
  ImpactList,
  ImpactRow,
  PreviewStatusView,
  countImpactRecords,
  toDisplay,
  useCorrectionPreview,
} from './correctionImpacts';
import type {
  DateFieldValue,
  PreviewCert,
  PreviewWarning,
  StatusChangeHandler,
} from './correctionImpacts';

type MoveOutPreviewCert = {
  ...PreviewCert,
  program: string,
  /** A Move-Out cert of the stay, or a completed cert effective on or after the new date. */
  reason: 'MOVE_OUT_CERT' | 'EFFECTIVE_AFTER_MOVE_OUT',
};

type MoveOutCorrectionImpactsData = {
  currentMoveOutDate: string,
  newMoveOutDate: string,
  unit: { id: string, number: string },
  stay: { stayLeaseId: string, moveInDate: string },
  /** The stay's leases starting after the new date: listed, never written. */
  affectedLeases: Array<{
    id: string,
    startDate: string,
    endDate: ?string,
    correctedLeaseRecommended: boolean,
  }>,
  certifications: Array<MoveOutPreviewCert>,
  warnings: Array<PreviewWarning>,
};

type Props = {
  residentId: string,
  organizationId: string,
  propertyId: string,
  /** The date field's value; only previewed once the client rules pass. */
  proposedDate: DateFieldValue,
  /** YYYY-MM-DD; an unchanged date has nothing to preview. */
  currentMoveOutDate: ?string,
  /** The form's own validation (required, format, future) passed. */
  clientValid: boolean,
  /**
   * The status, with the YYYY-MM-DD date it answers (null when there is none) and the number of
   * records listed (0 unless valid).
   */
  onStatusChange: StatusChangeHandler,
};

/** The record-count header; it names the modal's scrolling impact region (aria-labelledby). */
export const MOVE_OUT_IMPACTS_COUNT_ID = 'mo-impacts-count-label';

/** The affected-record list of a valid Move-Out Date correction preview. */
const MoveOutImpactList = ({
  impacts,
}: {
  impacts: ?MoveOutCorrectionImpactsData,
}): Node => {
  const intl = useIntl();
  const recordCount = countImpactRecords(impacts);
  if (!impacts || !recordCount) {
    return null;
  }
  const { certifications, affectedLeases, warnings } = impacts;

  return (
    <ImpactList
      testIdPrefix="mo"
      countId={MOVE_OUT_IMPACTS_COUNT_ID}
      reviewBanner={intl.formatMessage(messages.reviewBanner)}
      recordCount={recordCount}
      warnings={warnings}
    >
      {certifications?.map((cert) => (
        <CertificationImpactRow
          key={cert.id}
          cert={cert}
          dataTest="mo-impact-cert"
          needsReviewDescription={intl.formatMessage(
            cert.reason === 'EFFECTIVE_AFTER_MOVE_OUT'
              ? messages.needsReviewAfterMoveOutDescription
              : messages.needsReviewDescription,
            { formName: cert.formName },
          )}
        />
      ))}
      {affectedLeases?.map((lease) => (
        <ImpactRow
          key={lease.id}
          dataTest="mo-impact-lease"
          title={intl.formatMessage(impactMessages.stayLeaseTitle, {
            date: toDisplay(lease.startDate),
          })}
          description={[
            intl.formatMessage(messages.affectedLeaseDescription),
            lease.correctedLeaseRecommended
              ? intl.formatMessage(impactMessages.correctedLeaseRecommended)
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          chip={intl.formatMessage(
            lease.correctedLeaseRecommended
              ? impactMessages.actionNeeded
              : impactMessages.needsReview,
          )}
          chipColor="warning"
        />
      ))}
    </ImpactList>
  );
};

/**
 * The affected-record list of the Edit Move-Out Date modal (household-mi-mo-date-changes). Once the
 * entered date passes the form's rules it asks the backend for the dry-run plan of that date for
 * the prior resident's final stay and lists only the impacts that apply. Reports its status so the
 * modal can gate Save on it.
 */
const MoveOutCorrectionImpacts = ({
  residentId,
  organizationId,
  propertyId,
  proposedDate,
  currentMoveOutDate,
  clientValid,
  onStatusChange,
}: Props): Node => {
  const { status, impacts, message, retry } =
    useCorrectionPreview<MoveOutCorrectionImpactsData>({
      queryKey: [
        'moveOutDateCorrectionPreview',
        organizationId,
        propertyId,
        residentId,
      ],
      proposedDate,
      currentDate: currentMoveOutDate,
      clientValid,
      fetchPreview: (date, signal) =>
        new ResidentService().previewMoveOutDateCorrection(
          residentId,
          organizationId,
          propertyId,
          date,
          signal,
        ),
      onStatusChange,
    });

  if (status !== 'valid') {
    return (
      <PreviewStatusView
        testIdPrefix="mo"
        status={status}
        message={message}
        onRetry={retry}
      />
    );
  }
  return <MoveOutImpactList impacts={impacts} />;
};

export default MoveOutCorrectionImpacts;
